/**
 * Единая точка входа в расчётный движок диагностики.
 *
 * Порядок: разбор текущего состояния → потолок → главный рычаг →
 * оценка цели (светофор) → три сценария.
 */

import { calculateCurrentState, calculateAdMetrics, toFiniteNumber } from './core';
import { calculateCeiling, findPrimaryLever, evaluateTarget, TRAFFIC_LIGHT } from './ceiling';
import { buildScenarios, pickRecommendedScenario } from './scenarios';
import { LEVER, LEVER_MAP, LEVERS_BY_RANK, TIMING_DISCLAIMER } from './levers';
import { CEILING_DISCLAIMER, getNicheBenchmarks } from '../../../data/diagnostic/benchmarks';
import { getNicheChannels } from '../../../data/diagnostic/channels';
import { getNiche } from '../../../data/diagnostic/niches';

/**
 * @param {object} input
 * @param {string} input.nicheId
 * @param {string} input.dealModelId
 * @param {number} input.revenue     выручка за период
 * @param {number} input.sales       число продаж за период
 * @param {number} input.leads       число обращений за период
 * @param {number} input.frequency   покупок на клиента за период
 * @param {number} input.targetRevenue цель по выручке
 * @param {number} input.adBudget    рекламный бюджет за период
 * @param {number} input.targetMonths за сколько месяцев нужно дойти до цели
 */
export const runDiagnostic = (input) => {
  const state = calculateCurrentState(input);

  if (!state.valid) {
    return { valid: false, errors: state.errors, warnings: state.warnings };
  }

  const { nicheId } = input;
  const ceiling = calculateCeiling(state, nicheId);
  const primaryLever = findPrimaryLever(state, nicheId);
  const targetRevenue = toFiniteNumber(input.targetRevenue, 0);
  const targetEvaluation = evaluateTarget(state, ceiling, targetRevenue);
  const scenarios = buildScenarios({ state, ceiling, targetRevenue, nicheId });
  const recommended = pickRecommendedScenario(scenarios);
  const adMetrics = calculateAdMetrics(state, input.adBudget);

  return {
    valid: true,
    warnings: state.warnings,
    input,
    niche: getNiche(nicheId),
    benchmarks: getNicheBenchmarks(nicheId),
    channels: getNicheChannels(nicheId),
    state,
    ceiling,
    primaryLever,
    targetRevenue,
    targetEvaluation,
    scenarios,
    recommended,
    adMetrics,
    disclaimers: {
      ceiling: CEILING_DISCLAIMER,
      timing: TIMING_DISCLAIMER,
    },
  };
};

/**
 * Пересчёт выручки при ручном изменении рычагов — для слайдеров «что если».
 *
 * @param {object} state результат calculateCurrentState
 * @param {object} deltas прирост по каждому рычагу в долях, например { p: 0.1 }
 */
export const simulateLevers = (state, deltas = {}) => {
  if (!state || !state.valid) return null;

  const applied = {};
  let multiplier = 1;

  Object.values(LEVER).forEach((leverId) => {
    const delta = toFiniteNumber(deltas[leverId], 0);
    const isAvailable = state.availableLevers.includes(leverId);
    const effective = isAvailable ? delta : 0;

    applied[leverId] = effective;
    multiplier *= 1 + effective;
  });

  const revenue = state.revenue * multiplier;

  return {
    applied,
    multiplier,
    revenue,
    gain: revenue - state.revenue,
    gainPercent: (multiplier - 1) * 100,
    levers: Object.values(LEVER)
      .filter((leverId) => state.availableLevers.includes(leverId))
      .map((leverId) => ({
        id: leverId,
        label: LEVER_MAP[leverId].label,
        currentValue: state.levers[leverId],
        newValue: state.levers[leverId] * (1 + applied[leverId]),
        delta: applied[leverId],
      })),
  };
};

export {
  LEVER,
  LEVER_MAP,
  LEVERS_BY_RANK,
  TRAFFIC_LIGHT,
  calculateCurrentState,
  calculateCeiling,
  findPrimaryLever,
  evaluateTarget,
  buildScenarios,
  pickRecommendedScenario,
};
